import { useEffect, useState } from "react";
import "@/App.css";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  User,
  Train,
  Luggage,
  MessageCircle,
  AlertTriangle,
  ArrowRight,
} from "lucide-react";

const POSTER_URL =
  "https://customer-assets.emergentagent.com/job_porter-connect-8/artifacts/gau50owu_ad-local.png";

const WHATSAPP_NUMBER_DISPLAY = "+91 63717 21503";
const WHATSAPP_NUMBER_RAW = "916371721503"; // used for wa.me/ links
const WHATSAPP_CHAT_LINK = `https://wa.me/${WHATSAPP_NUMBER_RAW}`;
const CALL_LINK = "tel:+916371721503";
const SENIOR_WA_MESSAGE =
  "Hi LOKALOKO! I need a Porter at Bhubaneswar Railway Station (BBS).\nCan you help me book my slot?";
const SENIOR_WA_LINK = `https://wa.me/${WHATSAPP_NUMBER_RAW}?text=${encodeURIComponent(SENIOR_WA_MESSAGE)}`;

/* ---------- UI atoms ---------- */
function Wordmark({ size = 30 }) {
  return (
    <span
      className="lk-wordmark"
      style={{ fontSize: size, letterSpacing: "-0.01em" }}
    >
      LOKALOKO
    </span>
  );
}

/* ---------- Time-based demand banner ---------- */
function slotsRemaining() {
  const now = new Date();
  const h = now.getHours();
  const day = now.getDate(); // 1-31, used as a daily randomizer seed

  // Date-hash: deterministic but looks random day-to-day (scattered -5..+2)
  const x = Math.sin(day * 78.233) * 10000;
  const frac = x - Math.floor(x);
  const offset = Math.floor(frac * 8) - 5;

  let base, minOffset;
  if (h >= 6 && h < 12)       { base = 12; minOffset = -5; } // morning: 7–14
  else if (h >= 12 && h < 17) { base = 7;  minOffset = -5; } // afternoon: 2–9
  else if (h >= 17 && h < 21) { base = 4;  minOffset = -2; } // evening: 2–6
  else                        { base = 2;  minOffset = -1; } // 9 PM onwards: 1–4

  return Math.max(1, base + Math.max(minOffset, offset)); // never below 1
}

function DemandBanner() {
  const slots = slotsRemaining();
  return (
    <div className="inline-block" data-testid="demand-banner">
      <div className="lk-font-display text-[15px] sm:text-base font-bold text-[#10B7E6]">
        🛑 High Demand Alert: We only accept 30 bookings per day.
      </div>
      <div className="lk-font-display text-[15px] sm:text-base font-bold text-[#F97316] mt-1">
        Only {slots} slots remaining for today.
      </div>
    </div>
  );
}

/* ---------- Nav ---------- */
function Nav() {
  return (
    <header className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
      <a href="#top" className="flex items-baseline gap-2" data-testid="nav-logo">
        <Wordmark size={30} />
      </a>
      <nav className="hidden md:flex items-center gap-8 text-sm text-[#0B1220]/80">
        <a href="#how" className="hover:text-[#10B7E6]" data-testid="nav-how">How it works</a>
        <a href="#book" className="hover:text-[#10B7E6]" data-testid="nav-book">Book</a>
        <a href="#faq" className="hover:text-[#10B7E6]" data-testid="nav-faq">FAQ</a>
      </nav>
      <div
        className="text-[13px] font-bold tracking-wide text-[#0B1220]"
        data-testid="bbs-only"
      >
        BBS only
      </div>
    </header>
  );
}

/* ---------- Hero ---------- */
function Hero() {
  return (
    <section id="top" className="relative overflow-hidden">
      <div className="relative z-[1] max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-4 sm:pt-10 pb-12 sm:pb-16 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center">
        <div className="lg:col-span-7 lk-fade-up">
          <DemandBanner />
          <h1 className="lk-font-display font-black tracking-tighter leading-[1.03] text-[36px] sm:text-6xl lg:text-[72px] text-[#0B1220] mt-5">
            Online Porter <br />
            Booking. <br />
            Bhubaneswar <br className="hidden sm:block" />Station.
          </h1>
          <p className="mt-6 text-[16.5px] sm:text-lg text-[#334155] max-w-xl leading-relaxed">
            Secure a verified LOKALOKO Porter at BBS.
          </p>
          <p className="mt-2 text-[14.5px] sm:text-[15px] text-[#334155]/85 max-w-xl leading-relaxed">
            Note: Due to our strict verification process, we have a limited number of Porters on duty. Pre-booking 24 hours in advance is highly recommended to guarantee your slot.
          </p>

          <ul className="mt-6 space-y-2 lk-font-display text-[17px] font-semibold text-[#0B1220]" data-testid="hero-trust">
            <li>Pre-book in under 30 seconds</li>
            <li>Verified LOKALOKO Porters · ₹296 Base Fare</li>
            <li>Pay after service, via UPI</li>
          </ul>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <a href="#book" className="lk-btn-primary inline-flex items-center" data-testid="hero-cta-book">
              Find a Porter <ArrowRight size={18} className="ml-2" />
            </a>
            <a
              href={WHATSAPP_CHAT_LINK}
              target="_blank"
              rel="noreferrer"
              className="lk-btn-ghost inline-flex items-center gap-2"
              data-testid="hero-cta-whatsapp"
            >
              <MessageCircle size={16} /> Chat on WhatsApp
            </a>
          </div>

          <p className="mt-4 text-[12.5px] font-semibold text-[#F97316] leading-snug max-w-[280px] sm:max-w-md" data-testid="hero-scarcity">
            ⏳ Check Availability: Our LOKALOKO Porters are currently operating at 85% capacity. Submit your details to confirm your booking before slots close.
          </p>
        </div>

        <div className="lg:col-span-5 lk-fade-up" style={{ animationDelay: "120ms" }}>
          <div
            className="relative rounded-[24px] overflow-hidden border-[6px] border-[#CFF2FB] shadow-[0_20px_48px_rgba(11,18,32,0.10)]"
            data-testid="hero-poster"
          >
            <img
              src={POSTER_URL}
              alt="LOKALOKO porter service Bhubaneswar Railway Station"
              className="w-full block"
              loading="eager"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- How it works ---------- */
function HowItWorks() {
  const steps = [
    { n: "01", title: "Fill 3 details", body: "Name, train, and how many bags." },
    { n: "02", title: "Tap Find a Porter", body: "You are instantly taken into WhatsApp with a pre-filled request." },
    { n: "03", title: "We confirm your porter", body: "Name, photo, and platform meeting point delivered to your chat." },
    { n: "04", title: "Pay after service", body: "Pay via UPI after your luggage reaches the agreed point." },
  ];
  return (
    <section id="how" className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-14 sm:py-20 relative z-[1]">
      <div className="lk-font-display text-[15px] font-bold text-[#10B7E6]">How it works</div>
      <h2 className="lk-font-display text-3xl sm:text-5xl font-bold tracking-tight text-[#0B1220] mt-2 max-w-3xl">
        Four steps between you and a{" "}
        <span className="text-[#10B7E6]">stress-free</span> platform arrival.
      </h2>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {steps.map((s, i) => (
          <div key={s.n} className="lk-card lk-card-lift p-6 sm:p-7" data-testid={`step-${i + 1}`}>
            <div className="lk-font-display font-black text-4xl text-[#10B7E6] leading-none">{s.n}</div>
            <div className="lk-font-display font-semibold text-lg mt-4 text-[#0B1220]">{s.title}</div>
            <p className="text-[14.5px] text-[#334155] mt-2 leading-relaxed">{s.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------- Simplified booking (4 fields + WhatsApp deep link) ---------- */
function BookingCard() {
  const [fullName, setFullName] = useState("");
  const [train, setTrain] = useState("");
  const [bags, setBags] = useState("1");
  const [error, setError] = useState("");

  const bagOptions = ["1", "2", "3", "4+"];

  const findPorter = (e) => {
    e.preventDefault();
    setError("");
    const name = fullName.trim();
    const tr = train.trim();
    if (!name || !tr) {
      setError("Please fill Name and Train Number.");
      return;
    }
    const message =
      `Hi LOKALOKO! I need a Porter at BBS Station.\n` +
      `Name: ${name}\n` +
      `Train: ${tr}\n` +
      `Bags: ${bags}\n` +
      `(I am attaching a photo of my luggage below so you can confirm my exact fixed price!)`;
    const url = `https://wa.me/${WHATSAPP_NUMBER_RAW}?text=${encodeURIComponent(message)}`;
    window.location.href = url;
  };

  return (
    <section id="book" className="relative z-[1] max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 py-14 sm:py-20">
      <div className="lk-font-display text-[15px] font-bold text-[#10B7E6]">Book your porter</div>
      <h2 className="lk-font-display text-3xl sm:text-5xl font-bold tracking-tight text-[#0B1220] mt-2">
        Three quick details. <span className="text-[#10B7E6]">One tap.</span>
      </h2>
      <p className="text-[#334155] mt-3 max-w-xl">
        We&apos;ll open WhatsApp with your details. Just add a photo of your luggage to instantly confirm your fixed price.
      </p>

      <form onSubmit={findPorter} className="lk-card mt-8 p-5 sm:p-8" data-testid="booking-form">
        <Field icon={User} label="Full name" testid="field-name">
          <input
            className="lk-input"
            placeholder="Your full name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            data-testid="input-name"
          />
        </Field>

        <Field icon={Train} label="Train number" testid="field-train" className="mt-5">
          <input
            className="lk-input"
            placeholder="e.g. 12843"
            value={train}
            onChange={(e) => setTrain(e.target.value)}
            data-testid="input-train"
          />
        </Field>

        <Field icon={Luggage} label="Number of bags" testid="field-bags" className="mt-5">
          <div
            className="grid grid-cols-4 gap-2 sm:gap-3"
            role="radiogroup"
            aria-label="Number of bags"
          >
            {bagOptions.map((opt) => {
              const active = bags === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setBags(opt)}
                  className={`py-3 rounded-xl border text-base font-bold transition ${
                    active
                      ? "bg-[#10B7E6] border-[#10B7E6] text-white shadow-[0_6px_16px_rgba(16,183,230,0.35)]"
                      : "bg-white border-[#E2E8F0] text-[#0B1220] hover:border-[#10B7E6]"
                  }`}
                  data-testid={`bag-option-${opt.replace("+", "plus")}`}
                >
                  {opt}
                </button>
              );
            })}
          </div>
          <p className="text-[13.5px] text-[#334155] mt-3 leading-relaxed">
            <span className="lk-font-display font-bold text-[#0B1220]">Base fare: ₹296</span> · Covers up to 4 standard bags with 1 LOKALOKO Porter. Got heavy or oversized luggage? We&apos;ll confirm your exact fixed price via photo.
          </p>
        </Field>

        {error && (
          <div className="mt-5 flex items-center gap-2 text-[#991B1B] bg-[#FEF2F2] border border-[#FECACA] rounded-xl px-4 py-3 text-sm" data-testid="form-error">
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        <button
          type="submit"
          className="lk-btn-primary w-full mt-7 inline-flex items-center justify-center text-base"
          data-testid="submit-find-porter"
        >
          Find a Porter <ArrowRight size={18} className="ml-2" />
        </button>

        <p className="text-xs text-[#334155] mt-3 text-center">
          You&apos;ll be redirected to WhatsApp with your details. Attach a photo of your luggage and press send.
        </p>

        {/* Senior-citizen shortcut */}
        <div className="mt-6 pt-6 border-t border-[#E2E8F0]" data-testid="senior-shortcut">
          <div className="lk-font-display font-semibold text-[15px] text-[#0B1220]">
            Senior Citizen or in a hurry? Skip the form:
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <a
              href={CALL_LINK}
              className="inline-flex items-center justify-center gap-2 py-4 px-4 rounded-xl border-2 border-[#10B7E6] text-[#0B1220] font-bold text-[15px] bg-white hover:bg-[#E8F8FD] transition"
              data-testid="senior-call"
            >
              <span className="text-lg">📞</span> Tap here to Call us directly
            </a>
            <a
              href={SENIOR_WA_LINK}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 py-4 px-4 rounded-xl border-2 border-[#10B7E6] text-[#0B1220] font-bold text-[15px] bg-white hover:bg-[#E8F8FD] transition"
              data-testid="senior-whatsapp"
            >
              <span className="text-lg">💬</span> Tap here to book via WhatsApp
            </a>
          </div>
        </div>
      </form>
    </section>
  );
}

function Field({ icon: Icon, label, children, testid, className = "" }) {
  return (
    <label className={`block ${className}`} data-testid={testid}>
      <span className="flex items-center gap-1.5 text-[12px] font-bold text-[#0B1220] mb-1.5">
        {Icon ? <Icon size={12} className="text-[#334155]" /> : null} {label}
      </span>
      {children}
    </label>
  );
}

/* ---------- FAQ ---------- */
function FAQ() {
  const items = [
    {
      q: "How do I book a LOKALOKO porter online at Bhubaneswar Railway Station?",
      a: "Fill the 3-field form (name, train, bag count) and tap Find a Porter. We open WhatsApp with your request already typed — you just press send. LOKALOKO confirms the assigned porter and meeting point with you on WhatsApp.",
    },
    {
      q: "What happens when I tap Find a Porter?",
      a: "You are redirected straight to a WhatsApp chat with LOKALOKO's official Business number. Your booking request is pre-filled, so all you have to do is press the send arrow.",
    },
    {
      q: "What happens if my train is late or delayed?",
      a: "No stress — we follow a Standard Handoff Protocol. If a delay is announced while you are already at the station, your porter safely transports your luggage to the executive lounge or air-conditioned waiting room, ensures you are comfortably seated, finishes the task and collects payment. You are never left waiting on a hot platform.",
    },
    {
      q: "How many bags can 1 LOKALOKO porter carry?",
      a: "1 LOKALOKO Porter can comfortably handle up to 4 standard-sized bags. If you have heavy boxes, heavy oversized bags, or more than 4 items, we will assign an Additional Porter for an additional flat fee to ensure your luggage is moved safely and quickly.",
    },
    {
      q: "How much does it cost?",
      a: "Our base fare is ₹296, which covers 1 LOKALOKO Porter for up to 4 standard bags. Have heavy cartons or heavy oversized luggage? Just send us a photo on WhatsApp during booking, and we will instantly give you a custom fixed price.",
    },
    {
      q: "How will I identify my porter?",
      a: "Your WhatsApp confirmation includes the LOKALOKO porter's name, photo, contact number, and meeting point.",
    },
  ];

  return (
    <section id="faq" className="relative z-[1] max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 py-14 sm:py-20">
      <div className="lk-font-display text-[15px] font-bold text-[#10B7E6]">Common questions</div>
      <h2 className="lk-font-display text-3xl sm:text-5xl font-bold tracking-tight text-[#0B1220] mt-2">
        Online LOKALOKO porter booking at BBS · <span className="text-[#10B7E6]">answered.</span>
      </h2>

      <div className="mt-10 lk-card p-2 sm:p-4">
        <Accordion type="single" collapsible className="w-full">
          {items.map((it, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b last:border-0 border-[#E2E8F0]" data-testid={`faq-${i + 1}`}>
              <AccordionTrigger className="px-3 sm:px-4 py-4 text-left lk-font-display font-semibold text-[#0B1220] hover:no-underline">
                {it.q}
              </AccordionTrigger>
              <AccordionContent className="px-3 sm:px-4 pb-5 text-[#334155] text-[15px] leading-relaxed">
                {it.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

/* ---------- Footer + Float ---------- */
function Footer() {
  return (
    <footer className="relative z-[1] bg-[#0B1220] text-[#CBD5E1]">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-14 grid grid-cols-1 sm:grid-cols-3 gap-8">
        <div>
          <Wordmark size={30} />
          <p className="mt-4 text-sm text-white/70 max-w-xs">
            Verified porter booking service exclusively at Bhubaneswar Railway Station (BBS).
          </p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-white/50 font-bold">Reach us</div>
          <div className="mt-3 text-sm space-y-1.5">
            <div>WhatsApp: <a href={WHATSAPP_CHAT_LINK} className="text-white underline underline-offset-2" data-testid="footer-whatsapp">{WHATSAPP_NUMBER_DISPLAY}</a></div>
            <div>Email: <a href="mailto:contactlokaloko@gmail.com" className="text-white underline underline-offset-2" data-testid="footer-email">contactlokaloko@gmail.com</a></div>
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-white/50 font-bold">Explore</div>
          <ul className="mt-3 text-sm space-y-1.5">
            <li><a href="#how" className="hover:text-white">How it works</a></li>
            <li><a href="#book" className="hover:text-white">Book</a></li>
            <li><a href="#faq" className="hover:text-white">FAQ</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-4 text-center text-xs text-white/50">
        © {new Date().getFullYear()} Caspina Solutions Pvt. Ltd. · LOKALOKO is a trademark of Caspina Solutions.
      </div>
    </footer>
  );
}

function WhatsAppFloat() {
  return (
    <a
      href={WHATSAPP_CHAT_LINK}
      target="_blank"
      rel="noreferrer"
      className="lk-whatsapp-float"
      data-testid="whatsapp-float"
    >
      <MessageCircle size={18} /> Chat with us
    </a>
  );
}

/* ---------- App ---------- */
function App() {
  useEffect(() => {
    document.title = "LOKALOKO — Book a Railway Porter Online";
  }, []);

  return (
    <div className="min-h-screen">
      <Nav />
      <Hero />
      <HowItWorks />
      <BookingCard />
      <FAQ />
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}

export default App;
